import SlotSelectionActionBar from './SlotSelectionActionBar';
import SlotsGrid, { SlotsGridHeader } from './SlotsGridView';
import SlotsListView from './SlotsListView';
import SlotsToolbar from './SlotsToolbar';
import { applyQueryFiltersToSlots } from './filters/applySlotsFilters';
import { AppSettings } from '@opanuj/shared/types';
import { Box, Flex, Separator } from '@chakra-ui/react';
import { daysOrder } from '@opanuj/shared/utils';
import { findSlotAvailabilityIssue } from '@opanuj/shared/slots';
import { slotDelete } from '../../../operations/admin/slotDelete';
import { useActiveTimeLabel } from './hooks/useActiveTimeLabel';
import { useDatabase } from '../../../hooks/useDatabase';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelectedSlotAutoScroll } from './hooks/useSelectedSlotAutoScroll';
import { useSelectedSlotRoute } from './hooks/useSelectedSlotRoute';
import { useSlotsFiltersUrlState } from './hooks/useSlotsFiltersUrlState';

export type SlotsLayoutProps = {
	slots: any[]
	settings: AppSettings
}

export type SlotsDisplayMode = 'grid' | 'list';

function SlotsLayout({ slots, settings }: SlotsLayoutProps) {
	const { filters, patchFilters } = useSlotsFiltersUrlState();
	const { selectedSlotId, setSelectedSlotId, toggleSelectedSlotId } = useSelectedSlotRoute();
	const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>([]);
	const database = useDatabase();
	const mainContentRef = useRef<HTMLDivElement | null>(null);
	const [viewMode, setViewMode] = useState<SlotsDisplayMode>('grid');
	const filteredSlots = useMemo(
		() => applyQueryFiltersToSlots(slots as any[], filters),
		[slots, filters]
	);
	const calendarDays = useMemo(() => {
		const daysInData = daysOrder.filter((day) =>
			filteredSlots.some((slot) => slot?.day === day)
		);
		return daysInData.length > 0 ? daysInData : daysOrder.slice(0, 6);
	}, [filteredSlots]);
	const selectedSlot = useMemo(
		() => filteredSlots.find((slot) => slot.id === selectedSlotId) ?? null,
		[filteredSlots, selectedSlotId]
	);
	const selectedSlots = useMemo(
		() => {
			if (selectedSlotIds.length > 0) {
				return selectedSlotIds
					.map((slotId) => filteredSlots.find((slot) => slot.id === slotId))
					.filter(Boolean) as any[];
			}
			if (selectedSlot) return [selectedSlot];
			return [];
		},
		[filteredSlots, selectedSlot, selectedSlotIds]
	);
	const visibleSlotCount = filteredSlots.length;
	const conflictingSlotsCount = selectedSlot
		? filteredSlots.filter(
			(slot) => slot.id !== selectedSlot.id && findSlotAvailabilityIssue(selectedSlot, [], [slot])
		).length
		: 0;

	useEffect(() => {
		if (selectedSlotIds.length > 1 && selectedSlotId) {
			setSelectedSlotId(null);
		}
	}, [selectedSlotIds, selectedSlotId, setSelectedSlotId]);

	useEffect(() => {
		if (!selectedSlotId) return;
		if (!slots.length) return;
		const visible = filteredSlots.some((slot) => slot.id === selectedSlotId);
		if (!visible) {
			setSelectedSlotId(null);
		}
	}, [filteredSlots, selectedSlotId, setSelectedSlotId, slots.length]);

	useSelectedSlotAutoScroll({
		containerRef: mainContentRef,
		selectedSlotId,
		enabled: viewMode === 'list',
		deps: [filteredSlots.length],
	});

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
				return;
			}
			if (event.key === 'Escape') {
				setSelectedSlotIds([]);
				setSelectedSlotId(null);
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [setSelectedSlotId]);

	const activeTime = useActiveTimeLabel({
		containerRef: mainContentRef,
		enabled: viewMode === 'grid',
	});
	const handleDeleteSelected = async (slotIds: string[]) => {
		for (const slotId of slotIds) {
			await slotDelete(database, { id: slotId });
		}
		setSelectedSlotIds([]);
		setSelectedSlotId(null);
	};

	return (
		<Flex direction="column" h="100%" minH={0}>
			<Box backgroundColor="white">
				<SlotsToolbar
					slots={slots}
					filters={filters}
					onFiltersChange={patchFilters}
					viewMode={viewMode}
					onViewChange={setViewMode}
					visibleSlotCount={visibleSlotCount}
					conflictingSlotsCount={conflictingSlotsCount}
					selectedSlotId={selectedSlotId}
				/>
				<Separator />
			</Box>
			<Box ref={mainContentRef} bg={viewMode === 'list' ? 'gray.50' : 'white'} flex={1} minH={0} overflowY="auto" p={2}>
				{viewMode === 'list' ? (
					<SlotsListView
						slots={filteredSlots}
						settings={settings}
						selectedSlotId={selectedSlotId}
						selectedSlotIds={selectedSlotIds}
						onSelectSlot={toggleSelectedSlotId}
						onToggleSlotSelection={(slotId) => {
							setSelectedSlotIds((current) => {
								if (current.includes(slotId)) {
									return current.filter((id) => id !== slotId);
								}
								return [...current, slotId];
							});
						}}
					/>
				) : (
					<>
						<SlotsGridHeader days={calendarDays} activeTime={activeTime} />
						<SlotsGrid
							slots={filteredSlots}
							settings={settings}
							selectedSlot={selectedSlot}
							selectedSlotId={selectedSlotId}
							onSelectSlot={toggleSelectedSlotId}
						/>
					</>
				)}
			</Box>
			<SlotSelectionActionBar
				slot={selectedSlot}
				availableSlots={filteredSlots}
				selectedSlots={selectedSlots}
				selectedSlotIds={selectedSlotIds}
				onDeleteSelected={handleDeleteSelected}
				onClearSelection={() => {
					setSelectedSlotIds([]);
					setSelectedSlotId(null);
				}}
			/>
		</Flex>
	);
}

export default SlotsLayout;